/*
  ConsentsPanel — spec §19 consent management.

  Rendered on the Health Record tab. Shows one row per consent type
  with a coloured status pill; managers/staff capture new consent or
  withdraw existing ones. Withdrawn/expired grants stay in the list
  for the audit trail (dimmed, strikethrough).
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Plus, Ban, Trash2, Loader2, PenSquare, FileSignature,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CONSENT_TYPES = [
  { value: "treatment",   label: "Treatment",   help: "General consent to receive treatment" },
  { value: "privacy",     label: "Privacy",     help: "Data collection & handling per Privacy Policy" },
  { value: "recording",   label: "Recording",   help: "Audio recording of consultations (gates SOAP recording)" },
  { value: "telehealth",  label: "Telehealth",  help: "Remote / video consultations" },
  { value: "glad",        label: "GLA:D",       help: "GLA:D Australia program registry data-sharing" },
  { value: "onero",       label: "Onero™",      help: "Onero program participation" },
  { value: "photography", label: "Photography", help: "Clinical photography (body charts, wounds)" },
];
const CONSENT_LABELS = Object.fromEntries(CONSENT_TYPES.map((t) => [t.value, t.label]));

const SOURCES = [
  { value: "verbal",        label: "Verbal (documented)" },
  { value: "paper_signed",  label: "Paper-signed" },
  { value: "digital",       label: "Digital signature" },
  { value: "intake_form",   label: "Intake form" },
  { value: "migrated",      label: "Migrated" },
];

const STATUS_TONE = {
  given:     "bg-emerald-50 text-emerald-800 border-emerald-300",
  withdrawn: "bg-red-50 text-red-800 border-red-300 line-through",
  expired:   "bg-amber-50 text-amber-800 border-amber-300",
};

function fmtDate(d) {
  if (!d) return "—";
  try {
    const p = new Date(d);
    if (!Number.isNaN(p.getTime())) return p.toLocaleDateString();
  } catch { /* fall through */ }
  return String(d);
}

export default function ConsentsPanel({ email }) {
  const { can } = useAuth();
  const canHardDelete = can("owner", "manager");
  const [rows, setRows] = useState([]);
  const [activeByType, setActiveByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefillType, setPrefillType] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);

  const encoded = encodeURIComponent(email || "");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.get(`/clients/${encoded}/consents`);
      setRows(r.data?.consents || []);
      setActiveByType(r.data?.active_by_type || {});
    } catch (e) {
      toast.error("Couldn't load consents", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [email, encoded]);

  useEffect(() => { load(); }, [load]);

  const displayed = showInactive ? rows : rows.filter((r) => r.is_active);
  const inactiveCount = rows.length - rows.filter((r) => r.is_active).length;

  async function hardDelete(row) {
    if (!confirm(`Delete the ${CONSENT_LABELS[row.consent_type] || row.consent_type} consent entirely? Prefer Withdraw for genuine changes of mind.`)) return;
    try {
      await api.delete(`/consents/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-3" data-testid="consents-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Consents
          {rows.length > 0 && (
            <span className="text-zinc-400 normal-case font-normal">
              · {Object.keys(activeByType).length} active
              {inactiveCount > 0 && ` · ${inactiveCount} historic`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {inactiveCount > 0 && (
            <button
              type="button"
              onClick={() => setShowInactive((s) => !s)}
              className={cn(
                "text-2xs px-2 py-1 rounded-md border transition-colors",
                showInactive
                  ? "bg-zinc-950 border-zinc-950 text-white"
                  : "border-zinc-300 text-zinc-600 hover:bg-zinc-100",
              )}
              data-testid="consent-toggle-inactive"
            >
              {showInactive ? "Hide historic" : `Show historic (${inactiveCount})`}
            </button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => { setPrefillType(null); setDialogOpen(true); }}
            data-testid="consent-add-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Capture consent
          </Button>
        </div>
      </div>

      {/* Missing-consent hints for common types the practice cares about */}
      {!loading && (
        <div className="flex flex-wrap gap-1.5">
          {CONSENT_TYPES.map((t) => (
            !activeByType[t.value] && (
              <button
                key={t.value}
                type="button"
                onClick={() => { setPrefillType(t.value); setDialogOpen(true); }}
                className="text-2xs px-2 py-1 rounded-full border border-dashed border-zinc-300 text-zinc-500 hover:bg-zinc-100 hover:border-zinc-400 transition-colors"
                title={t.help}
                data-testid={`consent-missing-${t.value}`}
              >
                <Plus className="h-3 w-3 inline mr-0.5" /> Add {t.label}
              </button>
            )
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-zinc-500 py-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3">
          {rows.length === 0
            ? "No consents captured yet. Use the buttons above to record consent for each applicable type."
            : "Only historic consents present; toggle 'Show historic' to view them."}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="consent-list">
          {displayed.map((r) => (
            <li
              key={r.id}
              className={cn(
                "rounded-md border p-3 flex items-start justify-between gap-3",
                r.is_active ? "border-zinc-200 bg-white" : "border-zinc-200 bg-zinc-50 opacity-75",
              )}
              data-testid={`consent-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-zinc-950">
                    {CONSENT_LABELS[r.consent_type] || r.consent_type}
                  </span>
                  <span className={cn(
                    "text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium",
                    STATUS_TONE[r.status] || STATUS_TONE.given,
                  )}>
                    {r.status}
                  </span>
                  {r.signed_by_client && (
                    <span className="text-2xs text-emerald-700 flex items-center gap-0.5">
                      <FileSignature className="h-3 w-3" /> Signed
                    </span>
                  )}
                </div>
                <div className="text-2xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                  <span>Given {fmtDate(r.given_at)}</span>
                  {r.expiry_date && <span>· Expires {fmtDate(r.expiry_date)}</span>}
                  {r.source && <span>· {SOURCES.find((s) => s.value === r.source)?.label || r.source}</span>}
                  {r.policy_version && <span>· v{r.policy_version}</span>}
                </div>
                {r.notes && (
                  <div className="text-[0.75rem] text-zinc-600 mt-1">{r.notes}</div>
                )}
                {r.status === "withdrawn" && r.withdrawal_reason && (
                  <div className="text-[0.75rem] text-red-700 mt-1">
                    Withdrawn: {r.withdrawal_reason}
                    {r.withdrawn_by?.name && <span className="text-red-500 ml-1">(by {r.withdrawn_by.name})</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.is_active && (
                  <button
                    type="button"
                    onClick={() => setWithdrawing(r)}
                    className="p-1 rounded hover:bg-red-50 text-red-700"
                    title="Withdraw"
                    data-testid={`consent-withdraw-${r.id}`}
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </button>
                )}
                {canHardDelete && (
                  <button
                    type="button"
                    onClick={() => hardDelete(r)}
                    className="p-1 rounded hover:bg-zinc-100 text-zinc-500"
                    title="Delete (audit-preferred: Withdraw instead)"
                    data-testid={`consent-delete-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialogOpen && (
        <CaptureConsentDialog
          email={email}
          prefillType={prefillType}
          onClose={() => { setDialogOpen(false); setPrefillType(null); }}
          onSaved={load}
        />
      )}
      {withdrawing && (
        <WithdrawConsentDialog
          consent={withdrawing}
          onClose={() => setWithdrawing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function CaptureConsentDialog({ email, prefillType, onClose, onSaved }) {
  const [form, setForm] = useState({
    consent_type: prefillType || "treatment",
    signed_by_client: false,
    expiry_date: "",
    notes: "",
    source: "verbal",
    policy_version: "",
  });
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  async function save() {
    setSaving(true);
    try {
      await api.post(`/clients/${encodeURIComponent(email)}/consents`, {
        ...form,
        expiry_date: form.expiry_date || null,
      });
      toast.success("Consent captured");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="consent-dialog">
        <DialogHeader>
          <DialogTitle>Capture consent</DialogTitle>
          <DialogDescription>
            Log what the client has agreed to and how it was captured. Recording consent
            unblocks the audio-recording gate for SOAP sessions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Consent type</Label>
              <select
                value={form.consent_type}
                onChange={change("consent_type")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="consent-field-type"
              >
                {CONSENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <select
                value={form.source}
                onChange={change("source")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="consent-field-source"
              >
                {SOURCES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Expiry date (optional)</Label>
              <Input type="date" value={form.expiry_date} onChange={change("expiry_date")} className="mt-1" data-testid="consent-field-expiry" />
            </div>
            <div>
              <Label className="text-xs">Policy version</Label>
              <Input value={form.policy_version} onChange={change("policy_version")} placeholder="1.0" className="mt-1" data-testid="consent-field-policy" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs" data-testid="consent-field-signed-wrap">
            <input
              type="checkbox"
              checked={form.signed_by_client}
              onChange={change("signed_by_client")}
              data-testid="consent-field-signed"
            />
            Client has provided a physical/digital signature
          </label>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={change("notes")} className="mt-1" data-testid="consent-field-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="consent-save-btn">
            {saving ? "Saving…" : "Capture consent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawConsentDialog({ consent, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (reason.trim().length < 3) { toast.error("Reason required (≥3 chars)"); return; }
    setSaving(true);
    try {
      await api.post(`/consents/${consent.id}/withdraw`, { reason: reason.trim() });
      toast.success("Consent withdrawn");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Withdraw failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="consent-withdraw-dialog">
        <DialogHeader>
          <DialogTitle>Withdraw consent</DialogTitle>
          <DialogDescription>
            The consent stays in the audit log with your reason attached.
            {consent.consent_type === "recording" && (
              <span className="block mt-1 text-red-700">
                <PenSquare className="h-3 w-3 inline mr-1" />
                Note: withdrawing recording consent will block new SOAP audio sessions
                until fresh consent is captured.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Reason</Label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Client requested opt-out of recordings on 23 Feb 2026"
            className="mt-1"
            data-testid="consent-withdraw-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={submit}
            disabled={saving || reason.trim().length < 3}
            data-testid="consent-withdraw-confirm"
          >
            {saving ? "Saving…" : "Withdraw consent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
